import i18next from 'i18next';
import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import {
	ChatMessage,
	PRIVATE_CHAT_UNLOCKED_EVENT,
	PRIVATE_MESSAGE_EVENT,
	PrivateChatUnlockedPacket,
} from '../../services/chatApi';
import { getSocket } from '../../services/socket';
import { RootState } from '../../store';
import {
	chatMessageReceived,
	chatMessagesRequest,
	chatUnlocked,
	chatUnreadCountRequest,
} from '../../store/chat/chatActions';
import { selectChatConversationById } from '../../store/chat/chatReducer';
import { openPrivateChat } from '../../utils/helpers/chatLinks';
import { showPrivateChatToast } from './showGameBroadcastToast';

/** The toast shows the start of the message, never more than a line or two. */
const PREVIEW_LENGTH = 80;

const preview = (text: string) =>
	text.length > PREVIEW_LENGTH ? `${text.slice(0, PREVIEW_LENGTH - 1).trimEnd()}…` : text;

const isChatMessage = (raw: any): raw is ChatMessage =>
	!!raw &&
	typeof raw.id === 'string' &&
	typeof raw.conversationId === 'string' &&
	typeof raw.senderId === 'string' &&
	typeof raw.text === 'string';

/**
 * Keeps the private chat live on every screen: the `privateMessage` and `privateChatUnlocked`
 * pushes go into the chat store wherever the player is, and each one gets a toast that opens the
 * chat - except a message for the chat already open, which the thread shows by itself.
 *
 * Also asks for the unread total when the player logs in and whenever the socket reconnects, since
 * pushes sent while it was down are lost.
 */
const PrivateChatListener = () => {
	const dispatch = useDispatch();
	const store = useStore<RootState>();
	const isLoggedIn = useSelector((s: RootState) => !!s.auth?.user);
	const playerId = useSelector((s: RootState) => s.auth?.user?.id || '');
	const username = useSelector((s: RootState) => s.auth?.user?.username || '');

	useEffect(() => {
		if (!isLoggedIn || !playerId || !username) return;

		dispatch(chatUnreadCountRequest());

		const socket = getSocket(String(playerId), String(username));
		const myId = String(playerId);

		const onPrivateMessage = (raw: unknown) => {
			if (!isChatMessage(raw)) return;
			const message = raw;

			dispatch(chatMessageReceived({ message, myId }));

			const state = store.getState();
			if (message.senderId === myId) return;
			if (state.chat.openConversationId === message.conversationId) return;

			const other = selectChatConversationById(state, message.conversationId)?.otherPlayer;
			const title = other?.username
				? i18next.t('T_SITE_CHAT_TOAST_MESSAGE_FROM', {
						defaultValue: 'New message from {{username}}',
						username: other.username,
					})
				: i18next.t('T_SITE_CHAT_TOAST_MESSAGE', { defaultValue: 'New private message' });

			showPrivateChatToast(title, preview(message.text), other?.avatarFileId, () =>
				openPrivateChat(message.senderId, other?.username),
			);
		};

		const onChatUnlocked = (raw: unknown) => {
			const packet = raw as PrivateChatUnlockedPacket | undefined;
			const other = packet?.otherPlayer;
			if (!packet?.conversationId || !other?.id) return;

			dispatch(chatUnlocked(packet));

			showPrivateChatToast(
				i18next.t('T_SITE_CHAT_TOAST_UNLOCKED', {
					defaultValue: 'Your private chat with {{username}} is now unlocked!',
					username: other.username,
				}),
				i18next.t('T_SITE_CHAT_TOAST_UNLOCKED_HINT', { defaultValue: 'Tap to say hello.' }),
				other.avatarFileId,
				() => openPrivateChat(other.id, other.username),
			);
		};

		const onReconnect = () => {
			dispatch(chatUnreadCountRequest());
			const openConversationId = store.getState().chat.openConversationId;
			if (openConversationId) dispatch(chatMessagesRequest({ conversationId: openConversationId }));
		};

		socket.on(PRIVATE_MESSAGE_EVENT, onPrivateMessage);
		socket.on(PRIVATE_CHAT_UNLOCKED_EVENT, onChatUnlocked);
		socket.io.on('reconnect', onReconnect);

		return () => {
			socket.off(PRIVATE_MESSAGE_EVENT, onPrivateMessage);
			socket.off(PRIVATE_CHAT_UNLOCKED_EVENT, onChatUnlocked);
			socket.io.off('reconnect', onReconnect);
		};
	}, [dispatch, store, isLoggedIn, playerId, username]);

	return null;
};

export default PrivateChatListener;
